// The filter bar of a list (module 10 U228; pipeline F5, B1955): the find field, then the drop-downs ("By company",
// "By owner", ...), each one you can type into, then "Clear all" once anything is set, and a count line, "12 of 191
// tasks". Drawn open, in the same place on every list page (SEARCH-AND-FILTER-PLAN-2026-10-04.md section 2). What is
// set lives in the address (useFilterParams), so the page owns the values and this draws them.
//
// It wears records.css's filter row (`.recs .filters`, the find field and the `.tg` chip), as Companies, People and
// Deals do, so a chip reads the same on every page; find.css adds only the count and Clear all.
import { ActionButton } from '../parts/ActionButton.js';
import type { ReactNode } from 'react';
import { FindField } from './FindField.js';
import { fold, Picker, type PickerOption } from './Picker.js';
import '../records/records.css';
import './find.css';

export interface BarPick {
  /** The chip's words when nothing is chosen: "By company". */
  label: string;
  /** The open box's label: "Find a company". */
  find: string;
  /** The option that takes the narrowing off: "Any company". */
  any: string;
  value: string;
  options: readonly PickerOption[] | null;
  onChange: (id: string) => void;
}

export function FindBar({ find, q, onFind, picks, shown, total, noun, onClearAll, extra, after, extraSet = false }: {
  /** The find field's label and placeholder: "Find a task". */
  find: string;
  q: string;
  onFind: (q: string) => void;
  picks: readonly BarPick[];
  /** How many rows the narrowing leaves, and how many there are; null while the list is still being read. */
  shown: number | null;
  total: number | null;
  /** The thing counted, one and many: ['task', 'tasks']. */
  noun: readonly [string, string];
  onClearAll: () => void;
  /** A page's own chips kept beside the drop-downs (Chats' All, Cobrain, Teams, External). */
  extra?: ReactNode;
  /** What a page draws after the drop-downs (Meetings' From and To). */
  after?: ReactNode;
  /** Whether one of those chips or `after` narrows the list (Companies' Clients, Meetings' dates), so the count and Clear all show. */
  extraSet?: boolean;
}) {
  const narrowed = q !== '' || picks.some((p) => p.value !== '') || extraSet;
  return (
    <div className={narrowed ? 'recs findbar narrowed' : 'recs findbar'}>
      <div className="filters">
        <FindField label={find} value={q} onChange={onFind} />
        <div className="chips" role="group" aria-label="Narrow the list">
          {extra}
          {picks.map((p) => <Picker key={p.label} label={p.label} find={p.find} any={p.any} value={p.value} options={p.options} onChange={p.onChange} />)}
          {after}
          {narrowed ? <ActionButton reading type="button" className="findbar-clear" onClick={onClearAll}>Clear all</ActionButton> : null}
        </div>
      </div>
      {total !== null ? (
        <p className="findbar-count" role="status">
          {narrowed && shown !== null
            ? <><b>{shown.toLocaleString('en-GB')}</b> of {total.toLocaleString('en-GB')} {total === 1 ? noun[0] : noun[1]}</>
            : <><b>{total.toLocaleString('en-GB')}</b> {total === 1 ? noun[0] : noun[1]}</>}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Whether a row's words hold every word typed, in any order, accents and case aside: "padel kambi" finds a task called
 * "Kambi: the padel feed". Used by the lists whose rows are all in the browser (Tasks, Meetings, Chats, Calendar).
 */
export function matchesWords(words: string, ...fields: ReadonlyArray<string | null | undefined>): boolean {
  const want = fold(words).split(/\s+/).filter(Boolean);
  if (want.length === 0) return true;
  const hay = fold(fields.filter(Boolean).join(' '));
  return want.every((w) => hay.includes(w));
}

/**
 * The options of a drop-down from the rows themselves, each with how many rows carry it, by name: what a list in the
 * browser offers, so an option never narrows the list to nothing. A row may carry several (a meeting's companies).
 */
export function tally<R>(rows: readonly R[], of: (r: R) => ReadonlyArray<{ id: string; name: string }> | { id: string; name: string } | null): PickerOption[] {
  const seen = new Map<string, PickerOption & { n: number }>();
  for (const r of rows) {
    const got = of(r);
    const list = got === null ? [] : Array.isArray(got) ? got : [got as { id: string; name: string }];
    for (const o of new Map(list.map((x) => [x.id, x] as const)).values()) {
      const had = seen.get(o.id);
      if (had) had.n += 1; else seen.set(o.id, { id: o.id, name: o.name, n: 1 });
    }
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'en-GB', { sensitivity: 'base' }));
}
